/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertColor, Box, IconButton, Select, SelectChangeEvent, Snackbar, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';

import {
  determineScheduleDate,
  getDateRange,
  getFormattedDates,
  getRescheduleShiftsErrorMessage,
  getStoredCustomerDetails,
  useDateRangeOptions,
} from '../../../../../Shared/Utilities/utils';
import { NumberOfRowsOptions } from '../../../../../Shared/Constants/App';

import DataGrid from '../../../../../Shared/Components/Common/DataGrid/DataGrid';
import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import UpdateShiftDateCard from '../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import ModifyPopUp from '../../../../Jobs/Components/JobDetails/ModifyPopUp';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import ScheduleColumns from '../CancelShift/ScheduleColumns';

import WorkOrdersApiService from '../../../../Jobs/Services/WorkOrdersService';
import { RescheduleWorkDayInput } from '../../../../../API';
import { DateRangeSelector } from '../../../../../Shared/Components/Common/DateRangeSelection/DateRangeSelection.Model';
import { JobManagementType } from '../../AllJobs/AllJobs.Model';

import { Labels } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import DateRangeSelection from '../../../../../Shared/Components/Common/DateRangeSelection/DateRangeSelection';

import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { SnackBarWrapper } from '../../../../Jobs/Components/JobDetailsOverView/JobDetailsOverview.styles';
import { DropdownWrapper } from '../../../../Jobs/Components/WorkTasks/JobDetailsTasks.styles';
import theme from '../../../../../Shared/Themes/theme';
import { ActionsColumn } from '../../../../../Shared/Components/Common/DataGrid/DataGrid.styles';
import WhiteTooltip from '../../../../../Shared/Components/Common/CommonStyle/Tooltip.Style';
import { PaginationStyle } from '../AllJobs.Style';
import { StyledPagination } from '../../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { DropDown, SelectInput } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';
import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { Icons } from '../../../../../Shared/Constants/Icons';

export type RescheduleWorkDaysNewInput = {
  workOrderId?: string | null;
  workDayId: string;
  newDate: string;
  startTime?: { hours: number; minutes: number } | null;
  endTime?: { hours: number; minutes: number } | null;
  endDateOffset?: number | null;
};

const RescheduleShift = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const [openUpdateShiftDate, setOpenUpdateShiftDate] = useState<boolean>(false);
  const [isStartTimeSelectOpen, setStartTimeSelectOpen] = useState(false);
  const [isEndTimeSelectOpen, setEndTimeSelectOpen] = useState(false);
  const [shiftData, setShiftData] = useState<any>({});
  const [isMultiSelectedRows, setIsMultiSelectedRows] = useState<boolean>(false);

  const [isRescheduleShiftPopUpOpen, setIsRescheduleShiftPopUp] = useState(false);

  const [isRescheduleShiftLoading, setIRescheduleShiftLoading] = useState<boolean>(false);

  const [rescheduleMultipleShiftData, setRescheduleMultipleShiftData] = useState<RescheduleWorkDaysNewInput[]>([]);
  const [rescheduleShiftData, setRescheduleShiftData] = useState<RescheduleWorkDaysNewInput>();

  const [woScheduleMasterId, setWoScheduleMasterId] = useState<string>();
  const [fromDate, setFromDate] = useState<string>();
  const [toDate, setToDate] = useState<string>();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');

  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeSelector>({
    fromDate: null,
    toDate: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isValueOpen, setValueOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { t } = useTranslation([
    'jobManagement',
    'messages',
    'altTexts',
    'taskRejectionAndRework',
    'noData',
    'calendar',
  ]);

  const location = useLocation();

  const navigate = useNavigate();

  const dateRangeOptions = useDateRangeOptions();

  const defaultDateRangeOption = '7';

  const toggleValue = () => {
    setValueOpen(!isValueOpen);
  };

  const fetchData = useCallback(
    async (pageNumber: number, limit: number, woScheduleMasterId?: string, fromDate?: string, toDate?: string) => {
      setIsLoading(true);
      try {
        const { fromDateYMDFormat, toDateYMDFormat } = getFormattedDates(fromDate, toDate);

        if (!isEmpty(fromDateYMDFormat) || !isEmpty(toDateYMDFormat)) {
          const scheduleDate = determineScheduleDate(fromDateYMDFormat, toDateYMDFormat);
          const schedulesByDateResponse = await WorkOrdersApiService.schedulesByDateV2(
            scheduleDate ?? '',
            '',
            false,
            '',
            woScheduleMasterId,
            !scheduleDate ? fromDateYMDFormat : '',
            !scheduleDate ? toDateYMDFormat : '',
            limit,
            pageNumber
          );

          setSchedules(schedulesByDateResponse?.data);
          setTotalCount(schedulesByDateResponse?.metaData?.totalCount);
        }
      } catch (error) {
        console.error(t('messages:errorFetchingJobs'), error);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedDateRange, woScheduleMasterId]
  );

  const handleOnUpdateShiftDate = (rescheduleWorkDayInput: any) => {
    setIsRescheduleShiftPopUp(true);
    if (rescheduleWorkDayInput?.rescheduleWorkDayInput) {
      setRescheduleMultipleShiftData(rescheduleWorkDayInput?.rescheduleWorkDayInput);
    } else {
      setRescheduleShiftData(rescheduleWorkDayInput);
    }
  };

  const handleShiftTimings = (data: any) => {
    setOpenUpdateShiftDate(true);
    setShiftData(data);
    setIsMultiSelectedRows(false);
    setRescheduleMultipleShiftData([]);
    setRescheduleShiftData({
      workOrderId: null,
      workDayId: '',
      newDate: '',
      startTime: null,
      endTime: null,
      endDateOffset: null,
    });
  };

  const renderActionsCell = (row: any) => {
    const { dataStatus } = row.original;
    const isEditable = dataStatus === 'A' && !row.original.isRescheduled;
    return (
      <ActionsColumn>
        <IconButton
          size="large"
          sx={{
            '&:hover': {
              background: isEditable ? theme.palette.primary.main : theme.palette.common.white,
            },
          }}
          onClick={() => {
            if (isEditable) {
              handleShiftTimings(row.original);
            }
          }}>
          <WhiteTooltip title={isEditable ? t('jobManagement:rescheduleShift') : t('jobManagement:alreadyRescheduled')}>
            <img
              src={Icons.EditBlackIcon}
              alt={t('altTexts:edit')}
              style={{
                cursor: isEditable ? 'pointer' : 'not-allowed',
                opacity: isEditable ? 1 : 0.3,
                height: '1.5rem',
                width: '1.5rem',
              }}
            />
          </WhiteTooltip>
        </IconButton>
      </ActionsColumn>
    );
  };

  const columns = ScheduleColumns({
    renderActionsCell: renderActionsCell,
  });

  const handleRescheduleShiftPopup = () => {
    setIsRescheduleShiftPopUp(!isRescheduleShiftPopUpOpen);
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const handleRescheduleWorkDayResponse = (rescheduleWorkDaysResponse: any): void => {
    if (
      !isEmpty(rescheduleWorkDaysResponse?.data?.rescheduleFailure ?? []) &&
      isEmpty(rescheduleWorkDaysResponse?.errors ?? [])
    ) {
      const errorMsg = `${t('rescheduleShiftDate:failedToRescheduleShifts')}. ${t(getRescheduleShiftsErrorMessage(rescheduleWorkDaysResponse?.data?.rescheduleFailure?.[0]?.errorCode ?? ''))}`;
      showSnackbar('error', errorMsg);
    } else if (
      !isEmpty(rescheduleWorkDaysResponse?.data?.rescheduleSuccess ?? []) &&
      isEmpty(rescheduleWorkDaysResponse?.data?.rescheduleFailure ?? []) &&
      isEmpty(rescheduleWorkDaysResponse?.errors ?? [])
    ) {
      showSnackbar('success', t('rescheduleShiftDate:shiftsReschedulingSuccessMsg'));
      setTimeout(() => {
        setIRescheduleShiftLoading(false);
        setIsRescheduleShiftPopUp(false);
        setOpenUpdateShiftDate(false);
        navigate('/all-schedules');
      }, 1000);
    } else if (!isEmpty(rescheduleWorkDaysResponse?.errors ?? [])) {
      showSnackbar('error', rescheduleWorkDaysResponse?.errors[0]?.message);
    }
  };

  const handleRescheduleShift = async () => {
    try {
      setIRescheduleShiftLoading(true);
      const { userId } = getStoredCustomerDetails() || {};
      const rescheduleMultipleWorkDays = rescheduleMultipleShiftData.map((shift) => ({
        ...shift,
        workDayId: shift.workDayId ?? '',
        newDate: shift.newDate ?? '',
        startTime: !isEmpty(shift?.startTime) ? `${shift?.startTime?.hours}:${shift?.startTime?.minutes}` : '',
        endTime: !isEmpty(shift?.endTime) ? `${shift?.endTime?.hours}:${shift?.endTime?.minutes}` : '',
        endDateOffset: Number(shift?.endDateOffset),
      }));

      const rescheduleWorkDay = [
        {
          ...rescheduleShiftData,
          workDayId: rescheduleShiftData?.workDayId ?? '',
          newDate: rescheduleShiftData?.newDate ?? '',
          startTime: !isEmpty(rescheduleShiftData?.startTime)
            ? `${rescheduleShiftData?.startTime?.hours}:${rescheduleShiftData?.startTime?.minutes}`
            : '',
          endTime: !isEmpty(rescheduleShiftData?.endTime)
            ? `${rescheduleShiftData?.endTime?.hours}:${rescheduleShiftData?.endTime?.minutes}`
            : '',
          endDateOffset: Number(rescheduleShiftData?.endDateOffset),
        },
      ];

      const rescheduleWorkDayInput: RescheduleWorkDayInput[] = !isEmpty(rescheduleMultipleShiftData)
        ? rescheduleMultipleWorkDays
        : rescheduleWorkDay;

      const workOrderId = isEmpty(rescheduleMultipleShiftData)
        ? rescheduleShiftData?.workOrderId
        : rescheduleMultipleShiftData?.[0]?.workOrderId;
      const rescheduleWorkDaysResponse = await WorkOrdersApiService.rescheduleWorkDaysEndPoint(
        workOrderId as string,
        rescheduleWorkDayInput,
        userId
      );

      handleRescheduleWorkDayResponse(rescheduleWorkDaysResponse);
      setIRescheduleShiftLoading(false);
      setIsRescheduleShiftPopUp(false);
      setOpenUpdateShiftDate(false);
      fetchData(currentPage, itemsPerPage, woScheduleMasterId as string, fromDate, toDate);
    } catch (error) {
      console.error(t('messages:errorFetchingJobs'), error);
      showSnackbar('error', 'Error occurred while rescheduling the shifts');
    }
  };

  const handleDateRangeChange = (dateRange: DateRangeSelector) => {
    setSelectedDateRange({
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    });
  };

  useEffect(() => {
    const fromDate = selectedDateRange?.fromDate ?? '';
    const toDate = selectedDateRange?.toDate ?? '';
    const woScheduleMasterId: string = location.state?.selectedRows
      ?.map((row: { woScheduleMasterId: string }) => row.woScheduleMasterId)
      ?.join(',');
    setWoScheduleMasterId(woScheduleMasterId);
    setFromDate(fromDate as string);
    setToDate(toDate as string);
    fetchData(currentPage, itemsPerPage, woScheduleMasterId, fromDate as string, toDate as string);
  }, [selectedDateRange]);

  useEffect(() => {
    const { fromDate, toDate } = getDateRange(7);
    setSelectedDateRange({
      fromDate: fromDate,
      toDate: toDate,
    });
  }, []);

  const handleChange = (event: any, value: number): void => {
    setCurrentPage(value);
    fetchData(value, itemsPerPage, woScheduleMasterId, fromDate, toDate);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchData(currentPage, newItemsPerPage, woScheduleMasterId, fromDate, toDate);
  };

  return (
    <>
      <PageTitle title={t('jobManagement:rescheduleShift')} />
      <CustomerContainer>
        <DropdownWrapper sx={{ justifyContent: 'flex-end', marginTop: '2rem', marginBottom: '2rem' }}>
          <DateRangeSelection
            onDateRangeChange={handleDateRangeChange}
            defaultOption={defaultDateRangeOption}
            options={dateRangeOptions}
            isExcludePastDate={false}
            isRescheduleShift={true}
            showSpecificRangeDates={{
              from: new Date('2023-01-01'),
              to: dayjs().endOf('month').toDate(),
            }}
          />
        </DropdownWrapper>
        <DataGrid
          columns={columns}
          data={schedules}
          enableRowSelect={true}
          enableColumnPinning={false}
          loader={isLoading}
          errorMessageTitle={t('noData:noDataYet')}
          errorMessageDescription={t('noData:comeBackLater')}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          type={JobManagementType.AllSchedules}
          isRescheduleShift={true}
          disableDifferentDateRows={true} // to disable the rows with same woScheduleMasterId but different dates
        />

        {!isLoading && totalCount > 10 && (
          <PaginationStyle>
            <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
              <StyledPagination
                count={Math.ceil(totalCount / itemsPerPage)}
                page={currentPage}
                onChange={(event, value) => {
                  handleChange(event, value);
                }}
                color="primary"
              />
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3rem' }}>
              <Typography>
                {t('dashboard:totalItems')}: {totalCount}
              </Typography>
              {totalCount > 10 && (
                <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <Typography>{t('dashboard:recordsPerPage')}:</Typography>
                  <DropDown>
                    <Box onClick={toggleValue}>
                      <Select
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        label="Records"
                        input={<SelectInput />}
                        IconComponent={() => (
                          <img
                            src={Icons.DropdownIcon}
                            style={{
                              transform: isValueOpen ? Labels.Rotate : 'none',
                              position: 'absolute',
                              right: '0.75rem',
                            }}
                          />
                        )}
                        open={isValueOpen}
                        onClose={toggleValue}
                        onOpen={toggleValue}>
                        {NumberOfRowsOptions.map((dataItem) => (
                          <Options key={dataItem.id} value={dataItem.value}>
                            {dataItem.label}
                          </Options>
                        ))}
                      </Select>
                    </Box>
                  </DropDown>
                </Box>
              )}
            </Box>
          </PaginationStyle>
        )}
        {selectedRows?.length > 0 && (
          <SnackBarWrapper>
            <Snackbar
              open
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              action={
                <Button
                  type={ButtonType.Primary}
                  label={t('jobManagement:rescheduleShift')}
                  onClick={() => {
                    if (selectedRows?.length === 1) {
                      setShiftData(selectedRows?.[0]);
                    } else {
                      const date = new Date();
                      const formattedDate = date.toISOString().split('T')[0];
                      setShiftData({ scheduleDate: formattedDate });
                    }
                    setOpenUpdateShiftDate(true);
                    setIsMultiSelectedRows(true);
                    setRescheduleMultipleShiftData([]);
                    setRescheduleShiftData({
                      workOrderId: null,
                      workDayId: '',
                      newDate: '',
                      startTime: null,
                      endTime: null,
                      endDateOffset: null,
                    });
                  }}
                />
              }
            />
          </SnackBarWrapper>
        )}

        <UpdateShiftDateCard
          openUpdateShift={openUpdateShiftDate}
          handleShiftDateClose={() => setOpenUpdateShiftDate(false)}
          handleOnConfirm={handleOnUpdateShiftDate}
          shiftData={shiftData}
          setStartTimeSelectOpen={setStartTimeSelectOpen}
          setEndTimeSelectOpen={setEndTimeSelectOpen}
          isStartTimeSelectOpen={isStartTimeSelectOpen}
          isEndTimeSelectOpen={isEndTimeSelectOpen}
          isMultiSelectedRows={isMultiSelectedRows}
          isMultipleRescheduleShift={true}
          selectedRows={selectedRows}
        />
        <ModifyPopUp
          open={isRescheduleShiftPopUpOpen}
          handleClose={handleRescheduleShiftPopup}
          handleOnCloseJob={handleRescheduleShift}
          loading={isRescheduleShiftLoading}
          heading={t('jobManagement:rescheduleShift')}
          text={t('jobManagement:rescheduleShiftPopupText')}
        />
        <SnackbarMessage
          open={snackbarOpen}
          successMessage={snackbarMessage}
          errorMessage={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </CustomerContainer>
    </>
  );
};

export default RescheduleShift;
